import { HttpClient, HttpResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { AssessmentPageInterface } from 'app/assessment/models';
import { AppStateInterface } from 'app/core/app-config/state/models';
import { RestConnectorService } from 'app/core/connector-services/rest-connector-service/rest-connector-service.abstract';
import { Recommendation, RecommendationInterface } from 'app/recommendation/models/recommendation.model';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { RecommendationTemplateInterface, RecommendationTemplateRuleInterface } from '../models';
import { RecommendationCategoryInterface } from '../models/recommendation-category.model';

import { RecommendationServiceFilters } from './recommendation.service';

@Injectable()
export class RecommendationRestConnectorService extends RestConnectorService {

  constructor(private readonly _httpClient: HttpClient,
              protected readonly _store: Store<AppStateInterface>) {
    super(_store);
  }

  public loadAll(recommendationFilters?: Readonly<RecommendationServiceFilters>): Observable<RecommendationInterface[]> {
    if(recommendationFilters) {
      return this.loadRecommendationsForUser(recommendationFilters.subjectId);
    }

    return this.loadRecommendationsForUser(undefined);
  }

  public loadRecommendationsForUser(userId?: number): Observable<RecommendationInterface[]> {
    const userIdFilter: number = (userId) ? userId : this._authenticatedUser$.value.id;

    return this._httpClient.get<RecommendationInterface[]>(
      `/api/users/${userIdFilter}/recommendations`,
      { observe: 'response' },
    )
    .pipe(
      map((httpResponse: HttpResponse<RecommendationInterface[]>) => httpResponse.body),
    );
  }

  public loadOpenRecommendationsForUser(userId?: number): Observable<RecommendationInterface[]> {
    const userIdFilter: number = (userId) ? userId : this._authenticatedUser$.value.id;

    return this._httpClient.get<RecommendationInterface[]>(
      `/api/users/${userIdFilter}/recommendations/open`,
      { observe: 'response' },
    )
    .pipe(
      map((httpResponse: HttpResponse<RecommendationInterface[]>) => httpResponse.body),
    );
  }

  public loadCompletedRecommendationsForUser(userId?: number): Observable<RecommendationInterface[]> {
    const userIdFilter: number = (userId) ? userId : this._authenticatedUser$.value.id;

    return this._httpClient.get<RecommendationInterface[]>(
      `/api/users/${userIdFilter}/recommendations/completed`,
      { observe: 'response' },
    )
    .pipe(
      map((httpResponse: HttpResponse<RecommendationInterface[]>) => httpResponse.body),
    );
  }

  public loadRecommendationTemplates(): Observable<Readonly<RecommendationTemplateInterface[]>> {
    return this._httpClient.get<RecommendationTemplateInterface[]>(
      'api/recommendations/templates',
      { observe: 'response' },
    )
    .pipe(
      map((httpResponse: HttpResponse<RecommendationTemplateInterface[]>) => {
        return httpResponse.body;
      }),
    );
  }

  public loadRecommendationTemplatesLibrary(): Observable<Readonly<RecommendationCategoryInterface[]>> {
    return this._httpClient.get<RecommendationCategoryInterface[]>(
      'api/recommendations/templates/library',
      { observe: 'response' },
    )
    .pipe(
      map((httpResponse: HttpResponse<RecommendationCategoryInterface[]>) => {
        return httpResponse.body;
      }),
    );
  }

  public loadAssessmentQuestions(): Observable<Readonly<AssessmentPageInterface[]>> {
    return this._httpClient.get<AssessmentPageInterface[]>(
      'api/assessments/questions',
      { observe: 'response' },
    )
    .pipe(
      map((httpResponse: HttpResponse<AssessmentPageInterface[]>) => {
        return httpResponse.body;
      }),
    );
  }

  public addEmployeeRecommendation(entity: RecommendationInterface): Observable<RecommendationInterface> {
    return this._httpClient.post<RecommendationInterface>(
      `/api/users/${entity.subject.id}/recommendations`,
      entity,
      { observe: 'response' },
    )
    .pipe(
      map((httpResponse: HttpResponse<RecommendationInterface>) => httpResponse.body),
    );
  }

  public updateEmployeeRecommendation(entity: RecommendationInterface): Observable<RecommendationInterface> {
    const userIdFilter: number = entity.subject.id;  // array payload
    // make a deep copy in case we need to set properties like .lastUpdated, etc.
    const entityUpdate: RecommendationInterface = { ...entity };
    return this._httpClient.put<RecommendationInterface>(
      `/api/users/${userIdFilter}/recommendationsone`,
      entityUpdate,
      { observe: 'response' },
    )
    .pipe(
      map((httpResponse: HttpResponse<RecommendationInterface>) => {

        let recommendation: RecommendationInterface;
        if (httpResponse.status === 204 && !httpResponse.body) {
          recommendation = entityUpdate;
        } else {
          recommendation = <RecommendationInterface>httpResponse.body;
        }

        return new Recommendation(recommendation);
      }),
    );
  }

  public updateEmployeeRecommendationArray(entity: RecommendationInterface): Observable<RecommendationInterface> {
    const userIdFilter: number = entity[0].subject.id;  // array payload
    // make a deep copy in case we need to set properties like .lastUpdated, etc.
    const entityUpdate: RecommendationInterface = entity;
    return this._httpClient.put<RecommendationInterface>(
      `/api/users/${userIdFilter}/recommendations`,
      entityUpdate,
      { observe: 'response' },
    )
    .pipe(
      map((httpResponse: HttpResponse<RecommendationInterface>) => {

        let recommendation: RecommendationInterface;
        if (httpResponse.status === 204 && !httpResponse.body) {
          recommendation = entityUpdate;
        } else {
          recommendation = <RecommendationInterface>httpResponse.body;
        }

        return new Recommendation(recommendation);
      }),
    );
  }

  public deleteEmployeeRecommendation(recommendation: RecommendationInterface): Observable<RecommendationInterface> {
    const userId: number = recommendation.subject.id;
    const recommendationId: number = recommendation.id;

    return this._httpClient.delete(
      `/api/users/${userId}/recommendations/${recommendationId}`,
      { observe: 'response' },
    ).pipe(
      map((httpResponse: HttpResponse<RecommendationInterface>) => {
        if (httpResponse.status !== 200) {
          return httpResponse.body;
        }
        return recommendation;
      }),
    );
  }

  public createRecommendationCategory(categoryData: RecommendationCategoryInterface): Observable<RecommendationCategoryInterface> {
    return this._httpClient.post<RecommendationCategoryInterface>(
      '/api/recommendations/templates/library/',
      categoryData,
      { observe: 'response' },
    ).pipe(
      map((httpResponse: HttpResponse<RecommendationCategoryInterface>) => {
        return httpResponse.body as RecommendationCategoryInterface;
      }),
    );
  }

  public updateRecommendationCategory(categoryData: RecommendationCategoryInterface): Observable<RecommendationCategoryInterface> {
    return this._httpClient.put<RecommendationCategoryInterface>(
      '/api/recommendations/templates/library/',
      categoryData,
      { observe: 'response' },
    ).pipe(
      map((httpResponse: HttpResponse<RecommendationCategoryInterface>) => {
        return httpResponse.body as RecommendationCategoryInterface;
      }),
    );
  }

  public deleteRecommendationCategory(categoryData: RecommendationCategoryInterface): Observable<RecommendationCategoryInterface> {
    const categoryId: number = categoryData.id;

    return this._httpClient.delete(
      `/api/recommendations/templates/library/${categoryId}`,
      { observe: 'response' },
    ).pipe(
      map((httpResponse: HttpResponse<any>) => {
        return categoryData;
      }),
    );
  }

  public createRecommendationTemplate(templateData: RecommendationTemplateInterface): Observable<RecommendationTemplateInterface> {
    return this._httpClient.post<RecommendationTemplateInterface>(
      '/api/recommendations/templates/',
      templateData,
      { observe: 'response' },
    ).pipe(
      map((httpResponse: HttpResponse<RecommendationTemplateInterface>) => {
        return httpResponse.body as RecommendationTemplateInterface;
      }),
    );
  }

  public updateRecommendationTemplate(templateData: RecommendationTemplateInterface): Observable<RecommendationTemplateInterface> {
    return this._httpClient.put<RecommendationTemplateInterface>(
      '/api/recommendations/templates/',
      templateData,
      { observe: 'response' },
    ).pipe(
      map((httpResponse: HttpResponse<RecommendationTemplateInterface>) => {
        return httpResponse.body as RecommendationTemplateInterface;
      }),
    );
  }

  public deleteRecommendationTemplate(templateData: RecommendationTemplateInterface): Observable<RecommendationTemplateInterface> {
    const templateId: number = templateData.id;

    return this._httpClient.delete(
      `/api/recommendations/templates/${templateId}`,
      { observe: 'response' },
    ).pipe(
      map((httpResponse: HttpResponse<any>) => {
        return templateData;
      }),
    );
  }

  public createRecommendationTemplateRule(templateData: RecommendationTemplateInterface,
                                          ruleData: RecommendationTemplateRuleInterface): Observable<RecommendationTemplateInterface> {
    const templateId: number = templateData.id;

    return this._httpClient.post<RecommendationTemplateRuleInterface>(
      `/api/recommendations/templates/${templateId}/rules`,
      ruleData,
      { observe: 'response' },
    ).pipe(
      map((httpResponse: HttpResponse<RecommendationTemplateInterface>) => {
        return httpResponse.body as RecommendationTemplateInterface;
      }),
    );
  }

  public updateRecommendationTemplateRule(templateData: RecommendationTemplateInterface,
                                          ruleData: RecommendationTemplateRuleInterface): Observable<RecommendationTemplateInterface> {
    const templateId: number = templateData.id;

    return this._httpClient.put<RecommendationTemplateRuleInterface>(
      `/api/recommendations/templates/${templateId}/rules`,
      ruleData,
      { observe: 'response' },
    ).pipe(
      map((httpResponse: HttpResponse<RecommendationTemplateInterface>) => {
        return httpResponse.body as RecommendationTemplateInterface;
      }),
    );
  }

  public deleteRecommendationTemplateRule(templateData: RecommendationTemplateInterface,
                                          ruleData: RecommendationTemplateRuleInterface): Observable<RecommendationTemplateInterface> {
    const templateId: number = templateData.id;
    const ruleId: number = ruleData.id;

    return this._httpClient.delete(
      `/api/recommendations/templates/${templateId}/rules/${ruleId}`,
      { observe: 'response' },
    ).pipe(
      map((httpResponse: HttpResponse<RecommendationTemplateInterface>) => {
        return httpResponse.body as RecommendationTemplateInterface;
      }),
    );
  }
}
